import React, { useEffect, useRef, useState, ReactNode, useContext } from "react";
import { connect } from "react-redux";
import { MGrid, MSelect, MSpinner, MSplitter } from "components/Shared";
import { MGridColumnDefinition } from "components/Shared/MGrid";
import { CustomerListResponse, PhoenixFormListItem } from "interfaces/Mueller.Business.Models";
import { getDesignerFormsList, createFormDefinition, copyFormDefinition } from "requests/phoenixForms";
import { getCustomerList } from "requests/customer";
import { MSelectOption } from "components/Shared/MSelect";
import { getSearchParameter } from "components/Shared/Functions";
import { withRouter } from "tools/routerCompat";
import type { RouteComponentProps } from "tools/routerCompat.d";
import PhoenixFormsListDetails from "./PhoenixFormsListDetails";
import useStateSorted from "hooks/useStateSorted";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "components/Shared/IconButton";
import { DATA_TYPE_DATETIME_WITHDOW, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_NUMBER } from "typeConstants/dataTypes";
import moment from "moment-timezone";
import { userHasRight } from "selectors/rights";
import { FORM_DESIGN_EDIT } from "typeConstants/rights";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import { CreateFormDefinitionRequest, FormDefinition, FormDefinitionCopyRequest } from "interfaces/Phoenix.FormsDesigner.Models";
import { HeaderDefinition } from "typeConstants/header/headerDefinitions";
import { clearHeader, updateHeader } from "actions/layout";

interface Props extends RouteComponentProps {
  onSaveFinished: () => void;

  // Router
  location: RouteComponentProps["location"];
  history: any;
  
  // Redux
  canEditFormDesign: boolean;
  updateHeader: (headerDefinitions: HeaderDefinition) => void;
  clearHeader: () => void;
}

const PhoenixFormsList = ({ location, canEditFormDesign, history, updateHeader, clearHeader }: Props) => {
  const mounted = useRef(true);
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const gridControlMethodsDownloadGrid = useRef<() => void>(null);
  const [_formsList, setFormsList] = useStateSorted<PhoenixFormListItem>(null, "name");
  let formsList = _formsList; // makes me feel better about myself
  const [formNameFilter, setFormNameFilter] = useState(getSearchParameter(location));
  const [showInactiveForms, setShowInactiveForms] = useState(false);
  const [isCreatingForm, setIsCreatingForm] = useState(false);

  const [customerList, setCustomerList] = useState<CustomerListResponse[]>();
  const [selectedCustomer, setSelectedCustomer] = useState<MSelectOption<number>>();

  const [selectedForm, setSelectedForm] = useState<PhoenixFormListItem>(null);

  useEffect(() => {
    getCustomerList(setCustomerList, mounted);
    updateHeader({
      buttons: [{
        name: "Rules / Recs",
        onClick: () => history.push("/phoenixforms-designer/rules-list"),
      }]
    });
    
    return () => {
      mounted.current = false;
      clearHeader();
    };
  }, []);

  const reloadFormsList = () => {
    setFormsList(null);
    getDesignerFormsList({ customerNumber: selectedCustomer?.value, includeInactive: showInactiveForms }, setFormsList, mounted);
  };

  useEffect(reloadFormsList, [selectedCustomer, showInactiveForms]);

  const onSelectedCustomerChanged = (selectedCustomer: MSelectOption<number>) => {
    setFormNameFilter(null);
    setSelectedCustomer(selectedCustomer);
  };

  const onCreateFormClicked = (copiedFromFormId: string = null, newFormVals: Partial<CreateFormDefinitionRequest> = null) => {
    if (!newFormVals)
      newFormVals = {
        category: "",
        name: "",
        description: "",
      };

    dialog.showCard({
      cardProps: {
        title: copiedFromFormId ? `Copying form ${newFormVals.name}` : "New Form",
        fields: [
          { key: "name", label: "Name", value: newFormVals.name },
          { key: "category", label: "Category", value: newFormVals.category },
          { key: "description", label: "Description", dataType: DATA_TYPE_MULTI_LINE_STRING, value: newFormVals.description },
        ],
        onlyEditable: true,
        onEditChanged: kvp => kvp.forEach(({ key, value }) => newFormVals[key] = value),
      },
      onYesClick: () => {
        setIsCreatingForm(true);
        createForm(newFormVals, copiedFromFormId);
      },
      onNoClick: dialog.hide,
      yesLabel: "Create",
      noLabel: "Cancel",
      dialogProps: { width: 500 },
    });
  };

  const createForm = (newFormVals: Partial<CreateFormDefinitionRequest>, copiedFromFormId: string = null) => {
    if (copiedFromFormId) {
      let copyRequest = {
        category: newFormVals.category,
        name: newFormVals.name,
        description: newFormVals.description,
      } as FormDefinitionCopyRequest;

      copyFormDefinition(
        copiedFromFormId,
        copyRequest,
        (formDef: FormDefinition) => history.push(`/phoenixforms-designer/${formDef.formDefinitionId}`),
        mounted
      );
    } else {
      createFormDefinition(
        newFormVals as CreateFormDefinitionRequest,
        (formDef: FormDefinition) => history.push(`/phoenixforms-designer/${formDef.formDefinitionId}`),
        mounted
      );
    }
  };

  const nonPnxFormDefault = (fieldName: keyof PhoenixFormListItem, defaultValue = "not provided") => (row: PhoenixFormListItem) => row.phoenixFormId ? row[fieldName] as string : defaultValue;

  const gridColDefs: MGridColumnDefinition<PhoenixFormListItem>[] = [
    { key: "name", name: "Form Name", dataSource: "name", width: 350 },
    { key: "category", name: "Category", dataSource: nonPnxFormDefault("category", "Non Phoenix"), width: 125 },
    { key: "description", name: "Description", dataSource: nonPnxFormDefault("description") },
    { key: "modifiedDate", name: "Last Update", dataSource: "modifiedDate", dataType: DATA_TYPE_DATETIME_WITHDOW },
    { key: "modifiedBy", name: "Updated By", dataSource: nonPnxFormDefault("modifiedBy"), width: 150 },
    { key: "publishDate", name: "Publish Date", dataSource: "publishDate", sortValue: nonPnxFormDefault("publishDate"), dataType: DATA_TYPE_DATETIME_WITHDOW },
    { key: "volume30", name: "30 Day Vol", dataSource: "volume30", width: 100, dataType: DATA_TYPE_NUMBER },
    { key: "content", name: "Content", dataSource: row => row.contentCodesReferenced?.length > 0 ? "Yes" : "No", width: 80 },
    { key: "inactiveDate", name: "Inactive Date", dataSource: "inactiveDate", dataType: DATA_TYPE_DATETIME_WITHDOW },
  ];

  let firstChild = <MSpinner />;
  
  if (formNameFilter && formsList) formsList = formsList.filter(f => f.name.toLocaleLowerCase().includes(formNameFilter.toLocaleLowerCase()));

  // the grid doesnt keep the selected row by default. we could save the selected index, but then a filter would mess that up. so we just do it by id instead
  // it slightly complicates things that we have 2 ids here haha
  const selectedRow = formsList?.map((f, i) => (f.availFormId === selectedForm?.availFormId && f.phoenixFormId === selectedForm?.phoenixFormId) ? i : null).filter(i => i !== null);

  if (customerList && !isCreatingForm) {
    firstChild = <>
      <div className="forms-list-header">
        <MSelect
          placeholder="Select Customer Account"
          options={customerList.map(c => ({ value: c.number, label: c.displayLabel }))}
          // @ts-ignore it has issues because onChange could be multi but this dropdown is not multi
          onChange={onSelectedCustomerChanged}
          value={selectedCustomer}
        />
        <FormControlLabel
          control={<Checkbox checked={showInactiveForms} onChange={(e) => setShowInactiveForms(e.target.checked)} />}
          label="Show Inactive Forms"
          className="show-inactive-forms"
        />
        {canEditFormDesign && <IconButton
          icon="add"
          onClick={() => onCreateFormClicked()}
          tooltip="Create New Form"
        />}
        <IconButton
          icon="cloud_download"
          onClick={() => gridControlMethodsDownloadGrid.current()}
          disabled={!gridControlMethodsDownloadGrid.current}
        />
      </div>
      {formNameFilter && <div className="form-name-filter"><IconButton icon="close" onClick={() => setFormNameFilter(null)} />Filtering by form name "{formNameFilter}"</div>}
      <MGrid
        isFetchingData={!formsList}
        columnDefinitions={gridColDefs}
        data={formsList}
        selectedRows={selectedRow}
        onRowClick={i => setSelectedForm(formsList[i])}
        getControlMethods={({ downloadGrid }) => gridControlMethodsDownloadGrid.current = downloadGrid}
        downloadFileName={`FormsList-${moment().format("YYYY.MM.DD-HH.mm.ss")}`}
      />
    </>;
  }

  let secondChild: ReactNode = null;

  if (selectedForm && !isCreatingForm) {
    secondChild = (
      <PhoenixFormsListDetails
        pnxFormId={selectedForm.phoenixFormId}
        availFormId={selectedForm.availFormId}
        onSaveFinished={reloadFormsList}
        onCopyFormClicked={() => onCreateFormClicked(selectedForm.phoenixFormId, { category: selectedForm.category, description: selectedForm.description, name: selectedForm.name })}
      />
    );
  }

  return (
    <MSplitter
      className="designer-forms-list"
      firstChild={firstChild}
      secondChildTitle={{
        title: `Details for ${selectedForm?.name}`,
        buttons: [
          { icon: "close", onClick: () => setSelectedForm(null) }
        ],
      }}
      secondChild={secondChild}
      firstChildInitialSize="40%"
    />
  );
};

const mapStateToProps = state => ({
  canEditFormDesign: userHasRight(FORM_DESIGN_EDIT)(state),
});

const mapDispatchToProps = dispatch => ({
  updateHeader: (headerDefinitions) => dispatch(updateHeader(headerDefinitions)),
  clearHeader: () => dispatch(clearHeader()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PhoenixFormsList));
